import { createShuffledDeck } from './cards';
import { MODES, PHASES } from './constants';
import type { GameState, Player } from './types';

export function createPlayers(mode: GameState['mode']): Player[] {
  if (mode === MODES.FOUR_PLAYER) {
    return [
      { id: 'P1', teamId: 'T1' },
      { id: 'P2', teamId: 'T2' },
      { id: 'P3', teamId: 'T1' },
      { id: 'P4', teamId: 'T2' },
    ];
  }

  return [
    { id: 'P1', teamId: 'P1' },
    { id: 'P2', teamId: 'P2' },
  ];
}

export function emptyGameState(mode: GameState['mode'] = MODES.TWO_PLAYER): GameState {
  const players = createPlayers(mode);
  const teamIds = Array.from(new Set(players.map((player) => player.teamId)));

  return {
    phase: PHASES.SETUP,
    mode,
    players,
    currentPlayerId: players[0].id,
    dealerOrStarterRule: 'clockwise_from_P1',
    deck: [],
    hands: Object.fromEntries(players.map((player) => [player.id, []])),
    tableLoose: [],
    builds: [],
    stays: [],
    collections: Object.fromEntries(teamIds.map((teamId) => [teamId, { cards: [] }])),
    royalPending: Object.fromEntries(players.map((player) => [player.id, []])),
    turnContext: {
      mustPlacePendingRoyal: false,
      lastAction: undefined,
      pendingAbsorption: null,
      log: [],
    },
    meta: {
      seed: null,
      roundIndex: 0,
      totalRounds: mode === MODES.TWO_PLAYER ? 2 : 1,
      nextPileId: 1,
      scores: {},
      lastCollectorPlayerId: null,
      lastCollectorTeamId: null,
      reservedRound2Cards: [],
    },
  };
}

export function createNewGame(mode: GameState['mode'] = MODES.TWO_PLAYER, seed?: string, starterPlayerId?: string): GameState {
  const base = emptyGameState(mode);
  const deck = createShuffledDeck(seed || `${Date.now()}`);
  const resolvedStarter = starterPlayerId && base.players.some((p) => p.id === starterPlayerId) ? starterPlayerId : base.players[0].id;

  return {
    ...base,
    deck,
    phase: PHASES.BETWEEN_ROUNDS,
    currentPlayerId: resolvedStarter,
    meta: {
      ...base.meta,
      seed: seed || null,
    },
  };
}

export function dealRound(state: GameState, roundIndex = state.meta.roundIndex): GameState {
  const next: GameState = {
    ...state,
    hands: Object.fromEntries(state.players.map((player) => [player.id, []])),
  };

  // 2p, 2-round rule: keep 2 cards separate during round 1 and only deal them when round 2 starts.
  // This keeps round-2 uncertainty while ensuring each player only plays from the 13 dealt cards.
  if (next.mode === MODES.TWO_PLAYER && next.meta.totalRounds === 2) {
    if (roundIndex === 0 && next.meta.reservedRound2Cards.length === 0) {
      const reserved: any[] = [];
      const first = next.deck.shift();
      const second = next.deck.shift();
      if (first) reserved.push(first);
      if (second) reserved.push(second);
      next.meta = { ...next.meta, reservedRound2Cards: reserved };
    }
    if (roundIndex === 1 && next.meta.reservedRound2Cards.length) {
      next.deck.unshift(...next.meta.reservedRound2Cards);
      next.meta = { ...next.meta, reservedRound2Cards: [] };
    }
  }

  const cardsPerPlayer = 13;
  for (let i = 0; i < cardsPerPlayer; i += 1) {
    state.players.forEach((player) => {
      const card = next.deck.shift();
      if (card) {
        next.hands[player.id].push(card);
      }
    });
  }

  return {
    ...next,
    phase: PHASES.PLAYING,
    meta: {
      ...next.meta,
      roundIndex,
    },
    turnContext: {
      ...next.turnContext,
      mustPlacePendingRoyal: next.royalPending[next.currentPlayerId]?.length > 0,
      lastAction: undefined,
      pendingAbsorption: null,
      log: [...next.turnContext.log, `Round ${roundIndex + 1} started.`],
    },
  };
}
